import { useState, useEffect } from "react";
import dayjs from "dayjs";
import { callFirebaseFunction } from "../firebaseio/firebaseFunctions";
import { RyeToggle } from "../rye/RyeToggle";
import { DollarAmount } from "../common/DollarAmount";
import { SettingRow } from "./SettingRow";
import { CancelSubscriptionSetting } from "./CancelSubscriptionSetting";
import { buildClasses } from "../utils/buildClasses";
import { BreadPayment } from "breadcommon";

interface SubscriptionInfo {
  subscriptionId: string | null;
  autoRenewEnabled: boolean | null;
  status: string | null;
  currentPeriodStart: number | null;
  currentPeriodEnd: number | null;
}

export function BillingSettings(): JSX.Element {
  return (
    <div>
      <SubscriptionInfoSection />
      <PaymentHistorySection />
    </div>
  );
}

// ============================================================================
// Subscription Info Section
// ============================================================================

function SubscriptionInfoSection(): JSX.Element {
  const [isFetching, setIsFetching] = useState(true);
  const [isTogglingAutoRenew, setIsTogglingAutoRenew] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] =
    useState<SubscriptionInfo | null>(null);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const result = await callFirebaseFunction("GetSubscriptionInfo", {});
        const data = result.data;

        if (data.success) {
          setSubscriptionInfo({
            subscriptionId: data.subscriptionId ?? null,
            autoRenewEnabled: data.autoRenewEnabled ?? null,
            status: data.status ?? null,
            currentPeriodStart: data.currentPeriodStart ?? null,
            currentPeriodEnd: data.currentPeriodEnd ?? null,
          });
        } else {
          setError(data.error_msg || "Failed to fetch subscription info");
        }
      } catch (err) {
        console.error("Failed to fetch subscription:", err);
        setError("Failed to load subscription information");
      } finally {
        setIsFetching(false);
      }
    };

    fetchSubscription();
  }, []);

  const handleToggleAutoRenew = async (newAutoRenewValue: boolean) => {
    if (!subscriptionInfo || subscriptionInfo.autoRenewEnabled === null) return;

    setIsTogglingAutoRenew(true);
    setError(null);

    try {
      const result = await callFirebaseFunction("SetAutoRenew", {
        autoRenew: newAutoRenewValue,
      });
      const data = result.data;

      if (data.success) {
        setSubscriptionInfo({
          ...subscriptionInfo,
          autoRenewEnabled: newAutoRenewValue,
        });
      } else {
        setError(data.error_msg || "Failed to update auto-renew setting");
      }
    } catch (err) {
      console.error("Failed to toggle auto-renew:", err);
      setError("Failed to update auto-renew setting");
    } finally {
      setIsTogglingAutoRenew(false);
    }
  };

  return (
    <div>
      <div className={buildClasses("mb-4")}>
        <h3
          className={buildClasses(
            "text-lg",
            "font-semibold",
            "text-on-surface-800",
          )}
        >
          Current Subscription
        </h3>
      </div>

      {isFetching ? (
        <div className={buildClasses("text-on-surface-600", "text-sm")}>
          Loading subscription information...
        </div>
      ) : !subscriptionInfo?.subscriptionId ? (
        <div className={buildClasses("text-on-surface-600", "text-sm")}>
          No subscription found.
        </div>
      ) : (
        <>
          {subscriptionInfo.status && (
            <SettingRow
              label="Status"
              description={formatSubscriptionStatus(subscriptionInfo.status)}
              picker={<div />}
            />
          )}
          {subscriptionInfo.currentPeriodEnd && (
            <SettingRow
              label="Current Period End Date"
              description={formatTimestamp(subscriptionInfo.currentPeriodEnd)}
              picker={<div />}
            />
          )}
          {subscriptionInfo.autoRenewEnabled !== null && (
            <SettingRow
              label="Auto-Renewal"
              description={
                isTogglingAutoRenew
                  ? "Updating..."
                  : subscriptionInfo.autoRenewEnabled
                    ? "Renews at the end of each billing period."
                    : "Ends after the current billing period."
              }
              picker={
                <RyeToggle
                  initIsChecked={subscriptionInfo.autoRenewEnabled}
                  onChange={handleToggleAutoRenew}
                  disabled={isTogglingAutoRenew}
                />
              }
            />
          )}
        </>
      )}

      {/* Shown even if the subscription failed to load: this is the only place
          users can delete their account. */}
      {!isFetching && (
        <CancelSubscriptionSetting disabled={isTogglingAutoRenew} />
      )}

      {error && (
        <div
          className={buildClasses(
            "p-4",
            "ml-4",
            "mr-4",
            "bg-red-50",
            "border",
            "border-red-200",
            "rounded-md",
            "text-red-800",
          )}
        >
          {error}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Payment History Section
// ============================================================================

function PaymentHistorySection(): JSX.Element {
  const [isFetching, setIsFetching] = useState(true);
  const [payments, setPayments] = useState<BreadPayment[]>([]);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const result = await callFirebaseFunction("GetPaymentHistory", {});
        const data = result.data;

        if (data.success) {
          setPayments(data.payments || []);
        } else {
          console.error("Failed to fetch payment history:", data.error_msg);
        }
      } catch (err) {
        console.error("Failed to fetch payment history:", err);
      } finally {
        setIsFetching(false);
      }
    };

    fetchPayments();
  }, []);

  return (
    <div className={buildClasses("mt-8")}>
      <div className={buildClasses("mb-4")}>
        <h3
          className={buildClasses(
            "text-lg",
            "font-semibold",
            "text-on-surface-800",
          )}
        >
          Payment History
        </h3>
      </div>

      {isFetching ? (
        <div className={buildClasses("text-on-surface-600", "text-sm")}>
          Loading payment history...
        </div>
      ) : payments.length === 0 ? (
        <div className={buildClasses("text-on-surface-600", "text-sm")}>
          No payment history found.
        </div>
      ) : (
        payments.map((payment) => (
          <SettingRow
            key={payment.id}
            label={formatPaymentLabel(payment)}
            description={formatPaymentDescription(payment)}
            picker={
              payment.receiptUrl ? (
                <a
                  href={payment.receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={buildClasses(
                    "text-sm",
                    "text-primary-600",
                    "hover:text-primary-700",
                    "underline",
                  )}
                >
                  View Receipt
                </a>
              ) : (
                <div />
              )
            }
          />
        ))
      )}
    </div>
  );
}

// ============================================================================
// Formatting Helpers
// ============================================================================

function formatTimestamp(timestamp: number): string {
  return dayjs(timestamp * 1000).format("MMM D, YYYY");
}

function formatSubscriptionStatus(status: string): string {
  switch (status) {
    case "active":
      return "Active";
    case "trialing":
      return "Trial";
    case "past_due":
      return "Past Due";
    case "canceled":
      return "Canceled";
    case "unpaid":
      return "Unpaid";
    case "incomplete":
      return "Incomplete";
    case "incomplete_expired":
      return "Incomplete (Expired)";
    default:
      return (
        status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")
      );
  }
}

function formatPaymentLabel(payment: BreadPayment): string {
  const dateStr = formatTimestamp(payment.created);
  const status = formatPaymentStatus(payment.status, payment.refunded);
  return `${dateStr} · ${status}`;
}

function formatPaymentStatus(status: string, refunded: boolean): string {
  if (refunded) {
    return "Refunded";
  }
  switch (status) {
    case "succeeded":
      return "Succeeded";
    case "pending":
      return "Pending";
    case "failed":
      return "Failed";
    default:
      return (
        status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")
      );
  }
}

function formatPaymentDescription(payment: BreadPayment): React.ReactNode {
  return (
    <div className={buildClasses("flex", "flex-row", "gap-2", "items-center")}>
      <DollarAmount
        n={payment.amount / 100}
        showDollarSign={true}
        includeDecimals={true}
        align="span"
        size="sm"
      />
      <span>·</span>
      {payment.description && (
        <>
          <span>
            {payment.description.replace(/update/i, "update/renewal")}
          </span>
          <span>·</span>
        </>
      )}
      {payment.cardBrand && payment.cardLast4 && (
        <>
          <span>
            {formatCardBrand(payment.cardBrand)} •••• {payment.cardLast4}
          </span>
        </>
      )}
      {payment.refunded && payment.amountRefunded > 0 && (
        <>
          <span>·</span>
          <span>Refunded:</span>
          <DollarAmount
            n={payment.amountRefunded / 100}
            showDollarSign={true}
            includeDecimals={true}
            align="span"
            size="sm"
          />
        </>
      )}
    </div>
  );
}

function formatCardBrand(brand: string): string {
  switch (brand.toLowerCase()) {
    case "visa":
      return "Visa";
    case "mastercard":
      return "Mastercard";
    case "amex":
      return "American Express";
    case "discover":
      return "Discover";
    case "jcb":
      return "JCB";
    case "diners":
      return "Diners Club";
    case "unionpay":
      return "UnionPay";
    default:
      return brand;
  }
}
